import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import ShowToast from "components/ShowToast";
import { fetchWrapper } from "utils/apiFetch";

// Bulk billpay payout — sends the WHOLE batch in ONE request to
// billpay_bulk_send_money (BulkCyrusSendMoney), which loops server-side:
// add-beneficiary + send-money per row, with fresh wallet reads between
// rows. Mirrors soft_bulk_payout_transaction's pattern for the existing
// Softmint bulk flow, instead of looping two HTTP calls per row client-side.
//
// Sheet columns required: Beneficiary Name, Account Number, IFSC Code,
// Amount, Consent
// Optional: Mobile, Aadhar Number, PAN Number, Aadhar Verified, Area, City,
// District, State, Pincode, Remark

const REQUIRED_HEADERS = ["Beneficiary Name", "Account Number", "IFSC Code", "Amount", "Consent"];
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const MIN_AMOUNT = 110;
const MAX_AMOUNT = 200000;

const truthy = (v) => ["1", "true", "yes", "y"].includes(String(v ?? "").trim().toLowerCase());

const emptyRowState = () => ({ rows: [], fileName: "" });

const BulkBillpayPayoutModal = ({ show, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [{ rows, fileName }, setState] = useState(emptyRowState());
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transectionMode, setTransectionMode] = useState("IMPS/NEFT/RTGS");

  if (!show) return null;

  const resetAndClose = () => {
    setState(emptyRowState());
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const downloadSample = () => {
    const sampleRows = [
      [
        "S.No", "Beneficiary Name", "Account Number", "IFSC Code", "Amount",
        "Consent", "Mobile", "Aadhar Number", "PAN Number", "Aadhar Verified",
        "Area", "City", "District", "State", "Pincode", "Remark",
      ],
      [
        1, "Ramesh Kumar", "123456789012", "SBIN0001234", 5000,
        "Yes", "9999999999", "", "", "Yes",
        "", "", "", "", "", "Agent Commission",
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleRows);
    ws["!cols"] = [
      { wch: 6 }, { wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 10 },
      { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
      { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 18 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Bill Pay");
    XLSX.writeFile(wb, "bulk_billpay_sample.xlsx");
  };

  const validateRow = (raw, index) => {
    const errors = [];
    const name = String(raw["Beneficiary Name"] ?? "").trim();
    const account = String(raw["Account Number"] ?? "").trim();
    const ifsc = String(raw["IFSC Code"] ?? "").trim().toUpperCase();
    const mobile = String(raw["Mobile"] ?? "").trim();
    const aadhar = String(raw["Aadhar Number"] ?? "").trim();
    const pan = String(raw["PAN Number"] ?? "").trim();
    const aadharVerified = truthy(raw["Aadhar Verified"]);
    const consent = truthy(raw["Consent"]);
    const area = String(raw["Area"] ?? "").trim();
    const city = String(raw["City"] ?? "").trim();
    const district = String(raw["District"] ?? "").trim();
    const stateVal = String(raw["State"] ?? "").trim();
    const pincode = String(raw["Pincode"] ?? "").trim();
    const remark = String(raw["Remark"] ?? "Bulk Bill Pay").trim();
    const amountRaw = raw["Amount"];
    const amount = Number(amountRaw);

    if (!name) errors.push("Beneficiary name is required");
    if (!account || !/^\d{6,20}$/.test(account)) errors.push("Account number looks invalid");
    if (!ifsc || !IFSC_REGEX.test(ifsc)) errors.push("IFSC code looks invalid");
    if (!amountRaw || Number.isNaN(amount)) errors.push("Amount is required");
    else if (amount < MIN_AMOUNT) errors.push(`Amount must be at least ₹${MIN_AMOUNT}`);
    else if (amount > MAX_AMOUNT) errors.push(`Amount cannot exceed ₹${MAX_AMOUNT}`);
    if (!consent) errors.push("Consent must be Yes for this beneficiary");

    return {
      sno: index + 1,
      name, account, ifsc, mobile, aadhar, pan, aadharVerified, consent,
      area, city, district, state: stateVal, pincode, remark,
      amount: Number.isNaN(amount) ? 0 : amount,
      errors,
      status: "pending", // pending | success | awaiting_confirmation | failed
      message: "",
      referenceNum: "",
      cyrusid: "",
    };
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const missing = REQUIRED_HEADERS.filter(
        (h) => !Object.prototype.hasOwnProperty.call(json[0] || {}, h)
      );
      if (json.length === 0 || missing.length) {
        ShowToast(
          json.length === 0 ? "The file has no data rows" : `Missing column(s): ${missing.join(", ")}`,
          "danger"
        );
        setState(emptyRowState());
        return;
      }

      const parsedRows = json.map((raw, i) => validateRow(raw, i));
      setState({ rows: parsedRows, fileName: file.name });
    } catch (err) {
      ShowToast("Could not read that file. Please upload a valid .xlsx/.csv", "danger");
      setState(emptyRowState());
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeRow = (sno) => {
    setState((prev) => ({ ...prev, rows: prev.rows.filter((r) => r.sno !== sno) }));
  };

  const validCount = rows.filter((r) => r.errors.length === 0).length;
  const invalidCount = rows.length - validCount;

  const submitBulk = async () => {
    const toSubmit = rows.filter((r) => r.errors.length === 0);
    if (toSubmit.length === 0) {
      ShowToast("No valid rows to submit", "danger");
      return;
    }

    setSubmitting(true);

    // one geolocation read for the whole batch, reused for every row's
    // add-beneficiary call server-side
    let latlong = "0,0";
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation
          ? navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          : reject(new Error("no geolocation"))
      );
      latlong = `${pos.coords.latitude},${pos.coords.longitude}`;
    } catch {
      // keep "0,0" fallback
    }

    // mark all submitted rows as "processing" up front, since this is now
    // one request handled server-side, not a client-visible per-row loop
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((r) =>
        toSubmit.some((t) => t.sno === r.sno) ? { ...r, status: "processing" } : r
      ),
    }));

    try {
      const payload = {
        transection_mode: transectionMode,
        payouts: toSubmit.map((row) => ({
          beneficiary_name: row.name,
          account_number: row.account,
          ifsc_code: row.ifsc,
          amount: row.amount,
          mobile: row.mobile,
          aadhar_number: row.aadhar,
          pan_number: row.pan,
          aadhar_verified: row.aadharVerified,
          consent: row.consent,
          area: row.area,
          city: row.city,
          district: row.district,
          state: row.state,
          pincode: row.pincode,
          remark: row.remark,
          latlong,
        })),
      };

      const res = await fetchWrapper("https://pro.mokshiq.in/billpay_bulk_send_money", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const results = res?.results || [];

      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => {
          const submittedIdx = toSubmit.findIndex((t) => t.sno === r.sno);
          if (submittedIdx === -1) return r;
          const rowResult = results[submittedIdx];
          if (!rowResult) return { ...r, status: "failed", message: "No result returned" };

          const status =
            rowResult.state === "success" ? "success" :
            rowResult.state === "pending" ? "awaiting_confirmation" : "failed";

          return {
            ...r,
            status,
            message: rowResult.message || "",
            referenceNum: rowResult.reference_num || "",
            cyrusid: rowResult.cyrusid || "",
          };
        }),
      }));

      const successCount = results.filter((r) => r.state === "success").length;
      const pendingCount = results.filter((r) => r.state === "pending").length;
      const failCount = results.filter((r) => r.state === "failed").length;

      ShowToast(
        `Bulk bill pay finished: ${successCount} succeeded, ${pendingCount} pending, ${failCount} failed`,
        failCount ? "warning" : pendingCount ? "warning" : "success"
      );

      if ((successCount || pendingCount) && onSuccess) onSuccess();
    } catch (err) {
      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((r) =>
          toSubmit.some((t) => t.sno === r.sno)
            ? { ...r, status: "failed", message: "Network / server error" }
            : r
        ),
      }));
      ShowToast("Bulk submission failed — network or server error", "danger");
    }

    setSubmitting(false);
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid #eee",
    gap: "12px",
  };
  const closeBtnStyle = {
    flex: "0 0 auto",
    width: "32px",
    height: "32px",
    minWidth: "32px",
    border: "none",
    background: "#f1f1f1",
    color: "#333",
    borderRadius: "50%",
    fontSize: "18px",
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div className="modal-backdrop-custom" role="dialog" aria-modal="true">
      <div className="modal-panel bulk-payout-modal" style={{ maxHeight: "88vh", overflowY: "auto" }}>
        <div className="modal-header" style={headerStyle}>
          <h5 style={{ margin: 0, flex: "1 1 auto", minWidth: 0 }}>Bulk Bill Pay Payout</h5>
          <button type="button" style={closeBtnStyle} onClick={resetAndClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body">
          <div className="bulk-toolbar">
            <button className="btn outline" onClick={downloadSample} type="button">
              <i className="ph-duotone ph-download-simple" /> Download sample template
            </button>

            <label className="btn primary upload-btn">
              <i className="ph-duotone ph-upload-simple" /> Upload Excel / CSV
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                hidden
              />
            </label>

            <div className="mode-select">
              <label>Mode</label>
              <select value={transectionMode} onChange={(e) => setTransectionMode(e.target.value)}>
                <option value="IMPS/NEFT/RTGS">IMPS / NEFT / RTGS</option>
                <option value="IMPS">IMPS</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
              </select>
            </div>
          </div>

          <p style={{ fontSize: "13px", color: "#666", margin: "8px 0 0" }}>
            Each row registers a new Cyrus beneficiary first (₹7 charge per row), then sends the payout to it —
            you don't need an existing Beneficiary ID. The whole batch is submitted as one request and processed
            on the server.
          </p>

          {parsing && <div className="loader">Reading file...</div>}

          {fileName && !parsing && (
            <>
              <div className="bulk-summary">
                <span>{fileName}</span>
                <span className="pill valid">{validCount} valid</span>
                {invalidCount > 0 && <span className="pill invalid">{invalidCount} need fixing</span>}
              </div>

              <div className="bulk-table-wrap">
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Beneficiary</th>
                      <th>Account No</th>
                      <th>IFSC</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.sno} className={r.errors.length ? "row-invalid" : ""}>
                        <td>{r.sno}</td>
                        <td>{r.name}</td>
                        <td>{r.account}</td>
                        <td>{r.ifsc}</td>
                        <td>₹{r.amount || "-"}</td>
                        <td>
                          {r.errors.length > 0 && (
                            <span className="status-badge danger" title={r.errors.join("; ")}>
                              Fix: {r.errors[0]}
                            </span>
                          )}
                          {r.errors.length === 0 && r.status === "pending" && (
                            <span className="status-badge muted">Ready</span>
                          )}
                          {r.status === "processing" && (
                            <span className="status-badge info">Processing…</span>
                          )}
                          {r.status === "success" && (
                            <span className="status-badge success" title={r.referenceNum}>
                              Success
                            </span>
                          )}
                          {r.status === "awaiting_confirmation" && (
                            <span className="status-badge info" title={r.message}>
                              Pending — {r.message}
                            </span>
                          )}
                          {r.status === "failed" && (
                            <span className="status-badge danger" title={r.message}>
                              {r.message || "Failed"}
                            </span>
                          )}
                        </td>
                        <td>
                          {r.status === "pending" && r.errors.length === 0 && (
                            <button type="button" className="icon-btn" onClick={() => removeRow(r.sno)}>
                              <i className="ph-bold ph-trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {submitting && <span className="progress-text">Processing batch…</span>}
          <button type="button" className="btn outline" onClick={resetAndClose} disabled={submitting}>
            Close
          </button>
          <button type="button" className="btn primary" onClick={submitBulk} disabled={submitting || validCount === 0}>
            {submitting ? "Processing..." : `Submit ${validCount || ""} Payout${validCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkBillpayPayoutModal;